import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import React, {
  createRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { ReactCropperElement } from "react-cropper";
import { storage } from "../../firebaseConfig";
import { base64ToBlob, getFileUrl } from "../../hooks/utils";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { pushModal, resetModals } from "../../store/slices/modalsSlice";
import FileUploadProgressBar from "../dashboard/FileUploadProgressBar";
import ImageUploaderPopup from "./ImageUploaderPopup";
import { TutorFormDataI } from "./TutorRegistration";

const UploadTutorImage = ({
  step,
  title,
  imgType,
  formData,
  handleNext,
  handlePrev,
  setFormData,
}: {
  step: number;
  title: string;
  imgType: "profile_pic" | "logo";
  formData: TutorFormDataI;
  handleNext: () => void;
  handlePrev: (x: number) => void;
  setFormData: React.Dispatch<React.SetStateAction<TutorFormDataI>>;
}) => {
  const imageRef = useRef(null);
  const [error, setError] = useState("");
  const [fileProgress, setFileProgress] = useState(null);
  const dispatch = useAppDispatch();
  const {
    user: { uid },
  } = useAppSelector((state) => state.store);

  const [image, setImage] = useState<string>(formData[imgType]);
  const [cropData, setCropData] = useState("");
  const cropperRef = createRef<ReactCropperElement>();

  const getCropData = useCallback(() => {
    console.log("upload image - step 2");
    if (typeof cropperRef.current?.cropper !== "undefined") {
      setCropData(cropperRef.current?.cropper.getCroppedCanvas().toDataURL());
      console.log("upload image - step 3");
      dispatch(resetModals());
    } else {
      // the react-cropper package couldn't get the value of cropperRef or assign a value to it
      // this is a workaround to get the cropped image which is an instance of ReactCropperElement
      // which extends HTMLImageElement and adds a cropper (that has the cropper functionality) as a property to it
      // by using the DOM element directly
      // and to achieve that I had to clone the react-cropper package, edit and build it then use it in this project
      // you can find the updated package here: https://github.com/saifsalemm/qashqool-react-cropper
      const el = document.getElementById(
        "qashqool-react-cropper-img"
      ) as ReactCropperElement;
      setCropData(el?.cropper.getCroppedCanvas().toDataURL());
      console.log("upload image - step 4");
      dispatch(resetModals());
    }
  }, [cropperRef, dispatch]);

  const handleOpenPopup = useCallback(() => {
    dispatch(
      pushModal(
        <ImageUploaderPopup
          image={image}
          cropperRef={cropperRef}
          getCropData={getCropData}
        />
      )
    );
  }, [image, cropperRef, dispatch]);

  const onChange = (e: any) => {
    e.preventDefault();
    let files;
    if (e.dataTransfer) {
      files = e.dataTransfer.files;
    } else if (e.target) {
      files = e.target.files;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setImage(reader.result as any);
    };
    reader.readAsDataURL(files[0]);
  };

  const handleUploadFb = () => {
    console.log("upload image - step 6");
    if (image === formData[imgType]) return;

    const imageRef = ref(
      storage,
      `${imgType === "logo" ? "logos" : "profile_pics"}-${uid}`
    );
    const uploadTask = uploadBytesResumable(imageRef, base64ToBlob(cropData));

    console.log("upload image - step 7");
    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setFileProgress(progress.toFixed(2));
      },
      (error) => {
        console.log(error);
        setError("حدث خطأ");
      },
      () => {
        getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
          setFormData((prev) => ({
            ...prev,
            [imgType]: getFileUrl(downloadURL),
          }));
          setFileProgress(null);
        });
      }
    );
  };

  useEffect(() => {
    if (image !== formData[imgType] && !cropData) handleOpenPopup();
  }, [image]);

  useEffect(() => {
    console.log("upload image - step 5", cropData);

    if (cropData) handleUploadFb();
  }, [cropData]);

  useEffect(() => {
    setCropData("");
    setImage(formData[imgType]);
  }, [step]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (fileProgress) return;
        handleNext();
      }}
      className="text-center"
    >
      <h4 className="p-2 mb-4 mx-auto border-b-2 border-primary w-fit">
        {title}
      </h4>

      <div className="px-5 py-2 m-3">
        {fileProgress ? (
          <FileUploadProgressBar fileProgress={fileProgress} />
        ) : (
          <>
            <img
              src={formData[imgType]}
              alt="upload"
              className="w-full max-w-xs mx-auto border border-gray-300 rounded-lg my-2"
            />
            <input
              ref={imageRef}
              type="file"
              accept="image/jpeg, image/jpg, image/png"
              className="hidden"
              onChange={onChange}
            />
            <button
              type="button"
              className="bg-gray-100 border border-gray-300 text-sm px-4 py-2 rounded-lg hover:bg-gray-200 transition"
              onClick={() => {
                setCropData("");
                imageRef.current.click();
              }}
            >
              إضغط لتغيير الصورة
            </button>
          </>
        )}
      </div>

      <div className="flex justify-center gap-4 flex-wrap">
        <button
          type="button"
          className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 disabled:opacity-50"
          onClick={() => handlePrev(imgType === "profile_pic" ? 1 : 4)}
          disabled={!!fileProgress}
        >
          رجوع
        </button>

        <button
          type="submit"
          className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-(--primary-color-dark) disabled:opacity-50"
          disabled={!!fileProgress}
        >
          {!!fileProgress ? "التالي ..." : "التالي"}
        </button>
      </div>

      {!!error && (
        <div className="bg-red-100 text-red-800 rounded-lg w-full mt-3 p-3 text-center border border-red-300">
          {error}
        </div>
      )}
    </form>
  );

  // return (
  //   <form
  //     onSubmit={(e) => {
  //       e.preventDefault();
  //       if (fileProgress) return;
  //       handleNext();
  //     }}
  //   >
  //     <h4
  //       className="p-2 mb-4 mx-auto"
  //       style={{
  //         width: "fit-content",
  //         borderBottom: "2px solid #673de6",
  //       }}
  //     >
  //       {title}
  //     </h4>
  //     <div className="px-5 py-2 m-3 text-center">
  //       {fileProgress ? (
  //         <FileUploadProgressBar fileProgress={fileProgress} />
  //       ) : (
  //         <>
  //           <img
  //             src={formData[imgType]}
  //             alt="upload"
  //             className="img-fluid border-ea rounded-ea my-2"
  //           />
  //           <input
  //             ref={imageRef}
  //             type="file"
  //             accept="image/jpeg, image/jpg, image/png"
  //             className="d-none"
  //             onChange={onChange}
  //           />
  //           <button
  //             type="button"
  //             className="rounded-ea"
  //             onClick={() => {
  //               setCropData("");
  //               imageRef.current.click();
  //             }}
  //           >
  //             إضغط لتغيير الصورة
  //           </button>
  //         </>
  //       )}
  //     </div>
  //     <button
  //       type="button"
  //       className="btn btn-secondary rounded-ea me-2"
  //       onClick={() => handlePrev(imgType === "profile_pic" ? 1 : 4)}
  //       disabled={!!fileProgress}
  //     >
  //       رجوع
  //     </button>
  //     <button
  //       type="submit"
  //       className="rounded-ea"
  //       disabled={!!fileProgress}
  //     >
  //       {!!fileProgress ? "التالي ..." : "التالي"}
  //     </button>

  //     {!!error && (
  //       <div
  //         className="alert alert-danger rounded-ea w-100 mt-3 text-center"
  //         role="alert"
  //       >
  //         {error}
  //       </div>
  //     )}
  //   </form>
  // );
};

export default UploadTutorImage;
